import React from 'react';
import classnames from 'classnames/bind';
import { ErrorOutlinedIcon } from 'tuui';
import { TOOLTIP_POSITION, TooltipV2 as Tooltip } from 'taboola-ultimate-ui';
import { useConfigMatch, useIsAppAccountNetwork } from 'modules/taboola-common-frontend-modules/account-configurations';
import { FEATURE_FLAGS } from 'modules/taboola-common-frontend-modules/account-configurations';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { useTrackingState } from 'modules/tracking/hooks/useTrackingState';
import { usePixelWarningPermitted } from '../../campaigns/modules/common-campaign-form/components/MarketingObjective/usePixelWarningPermitted';
import { pixelOrS2sInstalled } from './pixelOrS2sInstalled';
import styles from '../components/NavigationItemContent/trackingNavigationItemContent.module.scss';

const classNameBuilder = classnames.bind(styles);

export const alertTypes = ({ isPixelOrS2sInstalled, hasAtLeastOneActiveConversion }) => {
    if (!isPixelOrS2sInstalled) {
        return 'alert';
    } else if (!hasAtLeastOneActiveConversion) {
        return 'warning';
    }
};

const alertTypesText = ({ isPixelOrS2sInstalled, hasAtLeastOneActiveConversion }) => {
    if (!isPixelOrS2sInstalled) {
        if (!hasAtLeastOneActiveConversion) {
            return {
                title: 'Pixel not installed and there are no conversions',
                text: 'For best performance and optimized results, please implement the pixel.',
            };
        }
        return {
            title: 'Pixel not installed but has conversions',
            text: 'For best performance and optimized results, please implement the pixel and enable at least one conversion rule',
        };
    }
    if (!hasAtLeastOneActiveConversion) {
        return {
            title: 'No active conversions',
            text: 'Your account has a live pixel, but no active conversions. Please define and enable your conversion(s)',
        };
    }
};

export const useTrackingBadgeAndTooltip = () => {
    const isDcoAccount = useConfigMatch({ [FEATURE_FLAGS.IS_DCO_ACCOUNT]: 'true' });
    const isNetworkAccount = useIsAppAccountNetwork();
    const isPixelWarningPermitted = usePixelWarningPermitted();
    const isPixelWarningsTesting = useConfigMatch({ [FEATURE_FLAGS.TA_PIXEL_WARNINGS_TESTING]: 'true' });

    const {
        trackingStateData: { pixelTrackingData, s2sTrackingData, hasAtLeastOneActiveConversion },
    } = useTrackingState(isPixelWarningPermitted);

    const isPixelOrS2sInstalled = pixelOrS2sInstalled(pixelTrackingData, s2sTrackingData);
    const { status: pixelStatus } = pixelTrackingData || {};

    const showBadgeAndTooltip =
        isPixelWarningPermitted &&
        isPixelWarningsTesting &&
        !isNetworkAccount &&
        !isDcoAccount &&
        (!isPixelOrS2sInstalled || !hasAtLeastOneActiveConversion);

    const badgeType = alertTypes({ isPixelOrS2sInstalled, hasAtLeastOneActiveConversion });
    const tooltipContent = alertTypesText({ isPixelOrS2sInstalled, hasAtLeastOneActiveConversion });

    return { showBadgeAndTooltip, badgeType, tooltipContent, pixelStatus };
};

export const AlertWrapper = ({ type }) => (
    <div className={classNameBuilder(styles['alert-wrapper'])}>
        <ErrorOutlinedIcon className={classNameBuilder(styles['alert-icon'], styles[type])} />
    </div>
);

export const TrackingBadgeTooltipComponent = ({ badgeType, tooltipContent, pixelStatus }) => {
    return (
        <Tooltip
            position={TOOLTIP_POSITION.RIGHT}
            arrow
            interactive
            className={styles['tooltip']}
            maskClassName={styles['tooltip-mask']}
            tailClassName={styles['tooltip-mask']}
        >
            <div className={styles['container']}>
                <ErrorOutlinedIcon className={classNameBuilder(styles['alert-icon'], styles[badgeType])} />
                <div className={styles['tooltip-text-container']}>
                    <span className={styles['tooltip-text-title']}>
                        <FormattedMessage
                            id={`tooltip.tracking.status.${pixelStatus}.title`}
                            defaultMessage={tooltipContent.title}
                        />
                    </span>
                    <FormattedMessage
                        id={`tooltip.tracking.status.${pixelStatus}`}
                        defaultMessage={tooltipContent.text}
                    />
                </div>
            </div>
        </Tooltip>
    );
};
